import React from 'react';
import colorMappings from '@upsales/components/Utils/colorMappings';
import './FinancialChart.scss';
import bemClass from '@upsales/components/Utils/bemClass';
import Hs from 'highcharts';
import moment from 'moment';
import { CurrencyFormat } from 'App/babel/utils/numberFormat';

type Props = {
	result: number[];
	turnover: number[];
	years: string[];
	placeholder: boolean;
	currency: string;
	chartOrder?: string;
};

interface Chart {
	new (...args: any): any;
}
const Highcharts = Hs as unknown as {
	Chart: Chart;
};

class FinancialChart extends React.Component<Props> {
	_chart?: HTMLDivElement | null;

	constructor(props: Props) {
		super(props);
	}

	componentDidMount() {
		this.renderChart();
	}

	componentDidUpdate() {
		this.renderChart();
	}

	renderChart() {
		const years = [...this.props.years];
		const result = [...this.props.result];
		const turnover = [...this.props.turnover];
		const chartOrder = this.props.chartOrder;

		while (years.length < 5) {
			years.push(
				moment(years[years.length - 1].toString() + '-01-01')
					.subtract(1, 'year')
					.year()
					.toString()
			);
			result.push(0);
			turnover.push(0);
		}

		if (chartOrder === 'asc') {
			years.reverse();
			result.reverse();
			turnover.reverse();
		}

		const currencyFormat = new CurrencyFormat(this.props.currency);
		if (this._chart) {
			let config;
			if (this.props.placeholder) {
				config = {
					tooltip: { enabled: false },
					chart: {
						type: 'column',
						backgroundColor: 'transparent',
						margin: [10, 30, 35, 60],
						height: 200,
						spacing: [0, 0, 0, 0]
					},
					title: { text: null },
					credits: { enabled: false },
					yAxis: {
						labels: {
							formatter: function () {
								return '';
							}
						},
						title: { text: null }
					},
					xAxis: {
						type: 'category',
						title: { text: null },
						lineWidth: 1,
						lineColor: colorMappings.get('grey-10'),
						tickWidth: 0,
						categories: [2020, 2019, 2018, 2017, 2016]
					},
					plotOptions: {
						series: {
							borderWidth: 0
						},
						column: {
							borderRadius: 3
						}
					},
					series: [
						{
							states: { hover: { enabled: false } },
							showInLegend: false,
							name: 'Turnover',
							color: colorMappings.get('grey-5'),
							data: [17, 13, 8, 7, 1],
							borderRadiusBottomLeft: '3px',
							borderRadiusBottomRight: '3px'
						},
						{
							states: { hover: { enabled: false } },
							showInLegend: false,
							name: 'Result',
							color: colorMappings.get('grey-5'),
							data: [10, 8, 7, 4, 1],
							borderRadiusBottomLeft: '3px',
							borderRadiusBottomRight: '3px'
						}
					]
				};
			} else {
				config = {
					chart: {
						type: 'column',
						backgroundColor: 'transparent',
						margin: [30, 30, 35, 100],
						spacingLeft: 100,
						height: 200,
						spacing: [0, 0, 0, 0]
					},
					title: { text: null },
					credits: { enabled: false },
					yAxis: {
						labels: {
							formatter: function () {
								if ((this as any).isFirst) {
									return '';
								}
								return currencyFormat.short((this as any).value);
							}
						},
						title: { text: null }
					},
					xAxis: {
						type: 'category',
						title: { text: null },
						lineWidth: 1,
						lineColor: colorMappings.get('grey-10'),
						tickWidth: 0,
						showEmpty: true,
						categories: years
					},
					plotOptions: {
						series: {
							borderWidth: 0
						},
						column: {
							borderRadius: 3
						}
					},
					tooltip: {
						borderColor: 'transparent',
						backgroundColor: '#1E252E',
						shadow: false,
						useHTML: true,
						formatter: function () {
							return `<div>
								<div class="Text Text--white">${currencyFormat.default((this as any).y)}</div>
							</div>`;
						},
						style: {
							zIndex: 100
						}
					},
					series: [
						{
							showInLegend: false,
							name: 'Turnover',
							color: colorMappings.get('website-visits-color'),
							data: turnover,
							borderRadiusBottomLeft: '3px',
							borderRadiusBottomRight: '3px'
						},
						{
							showInLegend: false,
							name: 'Result',
							color: colorMappings.get('bright-green'),
							negativeColor: colorMappings.get('red'),
							data: result,
							borderRadiusBottomLeft: '3px',
							borderRadiusBottomRight: '3px'
						}
					]
				};
			}
			new Highcharts.Chart(this._chart, config);
		}
	}

	render() {
		const classNames = new bemClass('FinancialChart');
		return <div className={classNames.b()} ref={r => (this._chart = r)} />;
	}
}

export default FinancialChart;
